import {
  AlternativeLOSection,
  SelectedLOItem,
  SelectedLOListItem,
} from "../types";

export const getLOChildId = (child: Record<string, any>): number | null => {
  const rawId =
    child.id ??
    child.activity_id ??
    child.lo_id ??
    child.learning_object_id ??
    child.id_learning_object;
  if (rawId == null || rawId === "") return null;
  const id = Number(rawId);
  return Number.isFinite(id) ? id : null;
};

export const mapLOChildrenById = (
  children: readonly Record<string, any>[],
): Map<number, Record<string, any>> => {
  const result = new Map<number, Record<string, any>>();
  children.forEach((child) => {
    const id = getLOChildId(child);
    if (id != null) result.set(id, child);
  });
  return result;
};

export const isAlternativeLOSection = (
  item: SelectedLOListItem,
): item is AlternativeLOSection => item.kind === "section";

export const flattenSelectedLOItems = (
  items: readonly SelectedLOListItem[],
): SelectedLOItem[] =>
  items.flatMap((item) => (isAlternativeLOSection(item) ? item.items : [item]));

const toStructureId = (value: unknown): number | null => {
  const rawId =
    typeof value === "object" && value !== null && "id" in value
      ? (value as { id: unknown }).id
      : value;
  if (rawId == null || rawId === "") return null;
  const id = Number(rawId);
  return Number.isFinite(id) ? id : null;
};

const toCompletionCriteria = (value: unknown) =>
  String(value ?? "").toLowerCase() === "required" ? "required" : null;

export const mapLOStructureToSelectedItems = <TItem extends SelectedLOItem>(
  structure: readonly unknown[],
  createItem: (id: number) => TItem,
): Array<TItem | AlternativeLOSection<TItem>> => {
  const result: Array<TItem | AlternativeLOSection<TItem>> = [];

  structure.forEach((rawEntry) => {
    if (typeof rawEntry !== "object" || rawEntry === null) return;

    const entry = rawEntry as Record<string, unknown>;
    const isSection =
      String(entry.type ?? "").toUpperCase() === "SECTION" ||
      Array.isArray(entry.lo);

    if (isSection) {
      const items = (Array.isArray(entry.lo) ? entry.lo : []).flatMap(
        (value) => {
          const id = toStructureId(value);
          return id == null
            ? []
            : [{ ...createItem(id), completion_criteria: null }];
        },
      );
      const sectionId = toStructureId(entry.id);
      result.push({
        ...(sectionId == null ? {} : { id: sectionId }),
        ...(typeof entry.title === "string" ? { title: entry.title } : {}),
        locked: true,
        kind: "section",
        items,
      });
      return;
    }

    const id = toStructureId(entry.id);
    if (id == null) return;
    result.push({
      ...createItem(id),
      completion_criteria: toCompletionCriteria(entry.completion_criteria),
    });
  });

  return result;
};

export const removeLOFromSection = <TItem extends SelectedLOItem>(
  items: readonly (TItem | AlternativeLOSection<TItem>)[],
  sectionIndex: number,
  loId: number,
): Array<TItem | AlternativeLOSection<TItem>> => {
  const result: Array<TItem | AlternativeLOSection<TItem>> = [];

  items.forEach((item, index) => {
    if (index !== sectionIndex || item.kind !== "section" || item.locked) {
      result.push(item);
      return;
    }

    const nextItems = item.items.filter((child) => child.id !== loId);
    if (nextItems.length > 0) {
      result.push({ ...item, items: nextItems });
    }
  });

  return result;
};

export const buildSelectedLOStructure = (
  items: readonly SelectedLOListItem[],
): Record<string, any>[] =>
  items.map((item) => {
    if (!isAlternativeLOSection(item)) {
      return {
        id: Number(item.id),
        type: "LO",
        lo: null,
        completion_criteria:
          item.completion_criteria === "required" ? "REQUIRED" : null,
      };
    }

    return {
      id: item.id ?? null,
      ...(item.title ? { title: item.title } : {}),
      type: "SECTION",
      lo: item.items.map((child) => Number(child.id)),
      completion_criteria: "ALTERNATIVE",
    };
  });
